import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import dayjs, { Dayjs } from 'dayjs';
import { Modal, Button, InputNumber, Input, Select, DatePicker, message } from '../../ui';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';
import { request } from '../../api/client';
import { inr2 } from '../../lib/format';
import { bankAcctOption } from '../billing/PaymentFormModal';

/**
 * "I PAID THIS INTO THE COMPANY'S BANK."
 *
 * The one action a collector needs and, until now, did not have. Removing the old hand-over workflow
 * left field staff holding retailer cash with no way to say where it went — the money could enter
 * their name and never leave it.
 *
 * Deliberately NOT a hand-over: nothing is declared to a PERSON, and no colleague has to agree that
 * they received anything. The rep is recording a fact about a BANK ACCOUNT — and that is exactly what
 * makes it checkable, so it does wait to be checked. It goes in as a claim, and either the reconciler
 * matching it to the statement line or Accounts confirming it against the account makes it real.
 *
 * Until then the rep stays accountable for the money. Anything else would let a person discharge their
 * own responsibility by typing a number, which is the one thing no other receipt in this system allows:
 * a UPI payment sits PENDING until the desk can see it, and cash out of a bag has fewer witnesses, not
 * more. That is also why the slip is mandatory here while a cash COLLECTION needs no proof at all — a
 * collection is witnessed by the retailer and carries the collector's name; a deposit is a claim about
 * a third party, and the third party's statement can contradict it.
 *
 * The amount is capped at what they are accountable for. Banking more than you hold would allocate to
 * nothing, sit as an unexplained receipt on the reconciliation screen, and read as though the same
 * collections had been banked twice.
 */
export function RecordDepositModal({ outstanding, onClose }: { outstanding: number; onClose: () => void }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState<number | undefined>(outstanding > 0 ? outstanding : undefined);
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [bankId, setBankId] = useState<string | undefined>();
  const [ref, setRef] = useState('');
  const [proofs, setProofs] = useState<string[]>([]);

  const { data: banksRaw } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
  });
  const banks = Array.isArray(banksRaw) ? banksRaw : ((banksRaw as any)?.data ?? []);

  const over = (amount ?? 0) > outstanding + 0.005;
  const ready = !!amount && amount > 0 && !over && !!date && !!bankId && proofs.length > 0;

  const save = useConfirmedMutation({ title: "Record this deposit?", content: "It goes to Accounts to verify before your cash custody is cleared.", okText: "Yes, record" }, {
    mutationFn: () => request({
      url: '/retailer-money/deposits',
      method: 'POST',
      data: {
        amount,
        depositedAt: date!.format('YYYY-MM-DD'),
        bankAccountId: bankId,
        referenceNo: ref.trim() || undefined,
        proofUrls: proofs,
      },
    }),
    onSuccess: () => {
      message.success('Deposit recorded — it stays against your name until it is confirmed in the bank');
      // Every figure this moves, refreshed together: the two books, the position, and the register.
      qc.invalidateQueries({ queryKey: ['my-money-ledger'] });
      qc.invalidateQueries({ queryKey: ['staff-retailer-money'] });
      qc.invalidateQueries({ queryKey: ['bank-register'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Could not record the deposit'),
  });

  const label = (text: string, required = false) => (
    <div className="mb-1 type-label text-muted">
      {text}{required && <span className="text-danger-600 dark:text-danger-500"> *</span>}
    </div>
  );

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      width={560}
      title="Record a deposit"
      description="Money you have paid into a company bank account. It stays against your name until Accounts or the bank statement confirms it."
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={!ready} onClick={() => save.mutate()}>
            Record deposit
          </Button>
        </>
      }
    >
      <div className="mb-4 rounded-card border border-line bg-surface-2/50 px-4 py-3">
        {/* The caller passes what is left AFTER anything already with the bank being checked, so this
            never invites a rep to record the same money twice while the first claim is in the queue. */}
        <div className="type-label text-muted">Collection money you can still bank</div>
        <div className="type-metric tabular-nums text-warning-600 dark:text-harvest-300">{inr2(outstanding)}</div>
        <div className="type-caption text-subtle">this is the most you can record as deposited</div>
      </div>

      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
        <div>
          {label('Amount', true)}
          <InputNumber min={1} className="w-full" value={amount}
            onChange={(v) => setAmount(v == null ? undefined : Number(v))} />
          {over && (
            <div className="mt-1 type-caption text-danger-600 dark:text-danger-500">
              More than you are accountable for. Raise anything extra with Accounts.
            </div>
          )}
        </div>
        <div>
          {label('Date paid in', true)}
          <DatePicker className="w-full" value={date} onChange={(v: any) => setDate(v)} />
        </div>
        <div className="sm:col-span-2">
          {label('Which company account', true)}
          <Select className="w-full" showSearch optionFilterProp="searchLabel" placeholder="Company bank account"
            value={bankId} onChange={setBankId} options={banks.map(bankAcctOption)}
            notFoundContent="No active company bank account" />
        </div>
        <div className="sm:col-span-2">
          {label('Slip / reference number')}
          <Input className="w-full" value={ref} onChange={(e) => setRef(e.target.value)}
            placeholder="Deposit slip or transfer reference" maxLength={120} />
        </div>
        <div className="sm:col-span-2">
          {label('Deposit slip', true)}
          <MultiFileUploadField category="receipt" buttonLabel="Attach slip / photo" value={proofs} onChange={setProofs} />
          <div className="mt-1 type-caption text-subtle">
            Required — the bank statement is what confirms this, and the slip is how it gets matched.
          </div>
        </div>
      </div>
    </Modal>
  );
}
